import { RefreshCw, Clock, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useIntegrationsList } from '@/lib/api/integrations'
import { useSyncDynamicSellingPlans } from '@/lib/api/products'
import { useToast } from '@/components/ui/use-toast'
import { useState } from 'react'
import { useTaskPolling } from '@/hooks/useTaskPolling'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'

interface SyncDynamicSellingPlansButtonProps {
    storeId: string
    onSyncComplete?: () => void
}

/**
 * Button component to sync dynamic selling plans from Shopify
 * Only displays if a Shopify integration is enabled for the store
 */
export const SyncDynamicSellingPlansButton = ({
    storeId,
    onSyncComplete,
}: SyncDynamicSellingPlansButtonProps) => {
    const { toast } = useToast()
    const { data: integrations } = useIntegrationsList(storeId)
    const syncMutation = useSyncDynamicSellingPlans()
    const [currentTaskId, setCurrentTaskId] = useState<string | null>(null)
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)

    const hasShopifyIntegration = integrations?.some(
        (integration) => integration.provider === 'shopify' && integration.enabled,
    )

    const { isPolling } = useTaskPolling({
        taskId: currentTaskId,
        onCompleted: () => {
            toast({
                title: 'Success',
                description: 'Dynamic selling plans sync completed!',
            })
            setCurrentTaskId(null)
            onSyncComplete?.()
        },
        onFailed: (error) => {
            toast({
                title: 'Error',
                description: `Sync failed: ${error}`,
                variant: 'destructive',
            })
            setCurrentTaskId(null)
        },
    })

    const handleSyncClick = () => {
        setShowConfirmDialog(true)
    }

    const handleConfirmSync = () => {
        setShowConfirmDialog(false)

        if (isPolling || syncMutation.isPending) return

        syncMutation.mutate(
            { storeId },
            {
                onSuccess: (data) => {
                    toast({
                        title: 'Sync Started',
                        description: data.message,
                    })
                    if (data.taskId) {
                        setCurrentTaskId(data.taskId)
                    }
                },
                onError: (error) => {
                    toast({
                        title: 'Sync Failed',
                        description: error.message,
                        variant: 'destructive',
                    })
                },
            },
        )
    }

    if (!hasShopifyIntegration) {
        return null
    }

    const isLoading = syncMutation.isPending || isPolling

    return (
        <>
            <Button
                onClick={handleSyncClick}
                disabled={isLoading}
                variant="outline"
                size="sm"
            >
                <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Syncing...' : 'Sync Selling Plans'}
            </Button>

            <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Clock className="h-5 w-5 text-orange-500" />
                            Confirm Selling Plans Sync
                        </AlertDialogTitle>
                        <AlertDialogDescription className="space-y-3 text-left">
                            <p className="font-medium text-foreground">
                                This will sync all dynamic selling plans from Shopify.
                            </p>

                            <div className="rounded-md bg-orange-50 border border-orange-200 p-3">
                                <p className="text-sm text-orange-900">
                                    <strong>⏱️ Note:</strong> This process may take a few minutes depending on the number of selling plans.
                                </p>
                            </div>

                            <div className="rounded-md bg-green-50 border border-green-200 p-3">
                                <p className="text-sm text-green-900">
                                    <AlertCircle className="inline h-4 w-4 mr-1" />
                                    <strong>No downtime:</strong> Your checkout will continue to work normally during the sync.
                                </p>
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleConfirmSync}>
                            Start Sync
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    )
}

